import React, { useRef } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'

import { RadiantSpinner } from 'components/RadiantSpinner'

import { purchaseUpgradeAction } from 'root-redux/actions/common'
import { selectUUID, selectUserSubscriptionInfo } from 'root-redux/selects/user'
import { TAppDispatch } from 'root-redux/store/store'

import { getBillingPeriodTranslation } from 'modules/unsubscribe/helpers'
import { OPTION_VALUES } from 'modules/unsubscribe/pages/DiscountOffer/constants'
import { useDiscountOffer } from 'modules/unsubscribe/pages/DiscountOffer/useDiscountOffer'

import { eventLogger } from 'services/eventLogger.service'

import { goTo } from 'browser-history'
import { PageId } from 'page-constants'
import { CENTS_IN_DOLLAR, CURRENCY_SYMBOLS } from 'root-constants'

import { FemiButton } from '../../components/FemiButton'
import { FemiFeedbackPopup } from '../../components/FemiFeedbackPopup'
import { FemiPageContainer } from '../../components/FemiPageContainer'
import {
  formatAmount,
  getDailyPrice,
  roundToNinetyNineCents,
} from '../../helpers'
import { useCancelSubscription } from '../../hooks/useCancelSubscription'
import { FemiBottomArea, FemiContent } from '../../styles'
import { StyledFemiDiscountOffer as S } from './FemiDiscountOffer.styles'

export const FemiDiscountOffer: React.FC = () => {
  const { search } = useLocation()
  const { t } = useTranslation()
  const dispatch: TAppDispatch = useDispatch()
  const userSubscriptionInfo = useSelector(selectUserSubscriptionInfo)
  const userId = useSelector(selectUUID)
  const { subscription: offer, discountPercentage } = useDiscountOffer()

  const {
    isFeedbackPopupShown,
    closeFeedbackPopup,
    cancelSubscription,
    goToCancellation,
  } = useCancelSubscription()
  const isSubmittedRef = useRef(false)

  const handleKeepPlan = () => {
    if (isSubmittedRef.current) return

    isSubmittedRef.current = true
    eventLogger.logDiscountOfferScreenClose(OPTION_VALUES.YES, userId)
    dispatch(purchaseUpgradeAction())
    goTo(
      { pathname: PageId.CONTINUED_SUBSCRIPTION, search },
      { isUpgrade: true },
    )
  }

  const handleCancelPlan = () => {
    eventLogger.logDiscountOfferScreenClose(OPTION_VALUES.NO, userId)
    goToCancellation()
  }

  if (!offer || !userSubscriptionInfo) {
    return (
      <FemiPageContainer hasNavigation>
        <RadiantSpinner />
      </FemiPageContainer>
    )
  }

  const { periodName, periodQuantity } = offer.mainPrices
  const offerCurrencySymbol = CURRENCY_SYMBOLS[offer.currency]
  const currentPrice =
    userSubscriptionInfo.subAmount || userSubscriptionInfo.price
  const currentDailyPrice = getDailyPrice(
    currentPrice,
    userSubscriptionInfo.interval,
    userSubscriptionInfo.intervalCount,
  )
  const offerPrice = offer.mainPrices.fullPrice
  const offerDailyPrice = getDailyPrice(offerPrice, periodName, periodQuantity)
  const offerOldPrice = offer.mainPrices.oldPrices.fullPrice
  const offerOldDailyPrice = getDailyPrice(
    offerOldPrice,
    periodName,
    periodQuantity,
  )
  const offerBillingPeriod = getBillingPeriodTranslation(
    periodName,
    periodQuantity,
  )
  const amountToPay = (offer.upgradeDiff?.amountToPay || 0) / CENTS_IN_DOLLAR

  return (
    <FemiPageContainer hasNavigation>
      <S.Content>
        <FemiContent>
          <S.Title>
            <Trans
              i18nKey="retention.femi.discountOffer.title"
              components={{ br: <br />, mark: <mark /> }}
            />
          </S.Title>
          <S.Description>
            {t('retention.femi.discountOffer.description', {
              percentage: discountPercentage,
            })}
          </S.Description>

          <S.Plans>
            <div>
              <S.Tag>{t('retention.femi.discountOffer.currentPlan')}</S.Tag>
              <S.CurrentPlanBody>
                <S.PlanInfo>
                  <S.PlanName>
                    {t('retention.femi.discountOffer.planName', {
                      context: userSubscriptionInfo.interval,
                      count: userSubscriptionInfo.intervalCount,
                    })}
                  </S.PlanName>
                  <S.PlanPrice>
                    {formatAmount(currentPrice, userSubscriptionInfo.currency)}
                  </S.PlanPrice>
                </S.PlanInfo>
                <S.Divider />
                <S.PricePerDay>
                  <S.DailyPrice>
                    {userSubscriptionInfo.currencySymbol}
                    {formatAmount(
                      currentDailyPrice,
                      userSubscriptionInfo.currency,
                    )}
                  </S.DailyPrice>
                  <S.PerDay>
                    {t('retention.femi.discountOffer.perDay')}
                  </S.PerDay>
                </S.PricePerDay>
              </S.CurrentPlanBody>
            </div>

            <div>
              <S.Tag isDark>
                {t('retention.femi.discountOffer.newPrice', {
                  percentage: discountPercentage,
                })}
              </S.Tag>
              <S.NewPlanBody>
                <S.PlanRow>
                  <S.Check />
                  <S.PlanInfo>
                    <S.PlanName>
                      {t('retention.femi.discountOffer.planName', {
                        context: periodName,
                        count: periodQuantity,
                      })}
                    </S.PlanName>
                    <S.PlanPrice>
                      <S.OldPrice>
                        {formatAmount(
                          roundToNinetyNineCents(offerOldPrice),
                          offer.currency,
                        )}
                      </S.OldPrice>
                      <span>→</span>
                      <span>{formatAmount(offerPrice, offer.currency)}</span>
                    </S.PlanPrice>
                  </S.PlanInfo>
                  <S.Divider />
                  <S.PricePerDay>
                    <S.PlanPrice>
                      <S.OldPrice>
                        {offerCurrencySymbol}
                        {formatAmount(offerOldDailyPrice, offer.currency)}
                      </S.OldPrice>
                    </S.PlanPrice>
                    <S.DailyPrice>
                      {offerCurrencySymbol}
                      {formatAmount(offerDailyPrice, offer.currency)}
                    </S.DailyPrice>
                    <S.PerDay>
                      {t('retention.femi.discountOffer.perDay')}
                    </S.PerDay>
                  </S.PricePerDay>
                </S.PlanRow>
                <S.Separator />
                <S.TodayRow>
                  <span>{t('retention.femi.discountOffer.youPayToday')}</span>
                  <span>
                    {offerCurrencySymbol}
                    {amountToPay
                      ? formatAmount(amountToPay, offer.currency)
                      : 0}
                  </span>
                </S.TodayRow>
                <S.Disclaimer>
                  <Trans
                    i18nKey="retention.femi.discountOffer.disclaimer"
                    values={{
                      price: `${offerCurrencySymbol}${formatAmount(
                        offerPrice,
                        offer.currency,
                      )}`,
                      period: t(offerBillingPeriod.key, {
                        count: offerBillingPeriod.count,
                      }),
                    }}
                  />
                </S.Disclaimer>
                <FemiButton type="button" onClick={handleKeepPlan}>
                  {t('retention.femi.discountOffer.keepPlan')}
                </FemiButton>
                <S.DiscountStart>
                  {t('retention.femi.discountOffer.discountStart')}
                </S.DiscountStart>
              </S.NewPlanBody>
            </div>
          </S.Plans>
        </FemiContent>
      </S.Content>
      <FemiBottomArea isFixed>
        <FemiButton
          type="button"
          variant="secondary"
          onClick={handleCancelPlan}
        >
          {t('retention.femi.discountOffer.cancelPlan')}
        </FemiButton>
      </FemiBottomArea>
      <FemiFeedbackPopup
        isShown={isFeedbackPopupShown}
        onComplete={cancelSubscription}
        onClose={closeFeedbackPopup}
      />
    </FemiPageContainer>
  )
}
